/**
 * Registrar Boleta - Backoffice Web Chofer
 * Path: backoffice-react/src/pages/chofer/NuevaBoletaChofer.jsx
 * Última modificación: 2026-01-03 17:50
 * Dev: cpalacios01@gmail.com | +595994648273
 * 
 * Permite al chofer registrar boletas desde el backoffice web
 * Nota: La impresión solo funciona en el dispositivo POS móvil
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Print as PrintIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const NuevaBoletaChofer = () => {
  const navigate = useNavigate();
  const [redondo, setRedondo] = useState(null);
  const [tarifas, setTarifas] = useState([]);
  const [selectedTarifa, setSelectedTarifa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [redondoRes, tarifasRes] = await Promise.all([
        axios.get('/api/redondos/activo'),
        axios.get('/api/tarifas'),
      ]);
      setRedondo(redondoRes.data);
      setTarifas(tarifasRes.data);
    } catch (err) {
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrar = async () => {
    if (!selectedTarifa) {
      setError('Debes seleccionar una tarifa');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await axios.post('/api/boletas', {
        redondo_id: redondo.id,
        tarifa_id: selectedTarifa.id,
        monto: selectedTarifa.monto,
        fecha_hora: new Date().toISOString(),
      });

      setSuccess(true);
      
      // Mostrar éxito por 2 segundos y volver
      setTimeout(() => {
        navigate('/chofer/dashboard');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al registrar boleta');
      setSubmitting(false);
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('es-PY').format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!redondo) {
    return (
      <Alert severity="warning">
        No tienes un redondo activo. Contacta al rendidor.
      </Alert>
    );
  }

  if (success) {
    return (
      <Box textAlign="center" py={6}>
        <CheckIcon sx={{ fontSize: 100, color: '#4CAF50', mb: 3 }} />
        <Typography variant="h4" fontWeight="bold" color="success.main" gutterBottom>
          ¡Boleta Registrada!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Redirigiendo...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Card sx={{ mb: 3, bgcolor: '#2196F3', color: 'white' }}>
        <CardContent>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Nueva Boleta de Pasaje
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Redondo
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {redondo.codigo_unico}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Móvil
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {redondo.bus?.numero}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Nota:</strong> La impresión automática solo está disponible en el dispositivo POS móvil.
          Desde el backoffice web, la boleta se registrará pero no se imprimirá.
        </Typography>
      </Alert>

      {/* Selección de Tarifa */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Selecciona el Pasaje
          </Typography>

          <FormControl component="fieldset" fullWidth sx={{ mt: 2 }}>
            <RadioGroup
              value={selectedTarifa?.id || ''}
              onChange={(e) => {
                const tarifa = tarifas.find(t => t.id === parseInt(e.target.value));
                setSelectedTarifa(tarifa);
              }}
            >
              {tarifas.map((tarifa) => (
                <Paper
                  key={tarifa.id}
                  elevation={selectedTarifa?.id === tarifa.id ? 4 : 1}
                  sx={{
                    p: 2,
                    mb: 2,
                    border: selectedTarifa?.id === tarifa.id ? '3px solid #4CAF50' : '1px solid #E0E0E0',
                    bgcolor: selectedTarifa?.id === tarifa.id ? '#E8F5E9' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: selectedTarifa?.id === tarifa.id ? '#E8F5E9' : '#F5F5F5',
                    },
                  }}
                  onClick={() => setSelectedTarifa(tarifa)}
                >
                  <FormControlLabel
                    value={tarifa.id}
                    control={<Radio />}
                    label={
                      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                        <Box>
                          <Typography variant="h6" fontWeight="bold">
                            {tarifa.nombre}
                          </Typography>
                          {tarifa.descripcion && (
                            <Typography variant="body2" color="text.secondary">
                              {tarifa.descripcion}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="h5" fontWeight="bold" color="primary" sx={{ ml: 2 }}>
                          Gs. {formatMoney(tarifa.monto)}
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%', m: 0 }}
                  />
                </Paper>
              ))}
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {/* Vista Previa */}
      {selectedTarifa && (
        <Card sx={{ mt: 3, border: '2px solid #2196F3' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <ReceiptIcon sx={{ fontSize: 60, color: '#2196F3', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Vista Previa del Ticket
            </Typography>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {selectedTarifa.nombre}
            </Typography>
            <Typography variant="h3" fontWeight="bold" color="primary" gutterBottom>
              Gs. {formatMoney(selectedTarifa.monto)}
            </Typography>
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E0E0E0' }}>
              <Typography variant="body2" color="text.secondary">
                Móvil: {redondo.bus?.numero}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {new Date().toLocaleString('es-PY')}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Botones de Acción */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          size="large"
          onClick={() => navigate('/chofer/dashboard')}
          disabled={submitting}
          sx={{ flex: 1 }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          size="large"
          startIcon={submitting ? <CircularProgress size={20} /> : <ReceiptIcon />}
          onClick={handleRegistrar}
          disabled={!selectedTarifa || submitting}
          sx={{
            flex: 2,
            bgcolor: '#4CAF50',
            '&:hover': { bgcolor: '#45a049' },
            fontSize: '1.1rem',
            py: 2,
          }}
        >
          {submitting ? 'Registrando...' : 'REGISTRAR BOLETA'}
        </Button>
      </Box>
    </Box>
  );
};

export default NuevaBoletaChofer;

// cpalacios01@gmail.com | +595994648273
